'use client';

import React, { useId, useCallback, useRef, useMemo } from 'react';
import { Radio } from '../../atoms/Radio';
import { Checkbox } from '../../atoms/Checkbox';
import './CardSelector.css';

export interface CardSelectorOption {
  /** Unique identifier for the option */
  id: string;
  /** Label content - can be text or React elements */
  label: React.ReactNode;
  /** Optional description shown below the label */
  description?: string;
  /** Optional icon shown before the label */
  icon?: React.ReactNode;
  /** Whether this option is disabled */
  disabled?: boolean;
}

export interface CardSelectorProps {
  /** Array of selectable options */
  options: CardSelectorOption[];
  /** Currently selected value(s) - string for single, string[] for multiple */
  value?: string | string[];
  /** Callback when selection changes */
  onChange?: (value: string | string[]) => void;
  /** Allow multiple selections (checkbox mode) */
  multiple?: boolean;
  /** Number of columns in the grid */
  columns?: 1 | 2 | 3 | 4;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Name attribute for form submission */
  name?: string;
  /** Additional CSS class */
  className?: string;
  /** Disable all options */
  disabled?: boolean;
  /** Optional label for the fieldset */
  legend?: string;
  /** Hide the legend visually (still accessible to screen readers) */
  legendHidden?: boolean;
}

export const CardSelector: React.FC<CardSelectorProps> = ({
  options,
  value,
  onChange,
  multiple = false,
  columns = 2,
  size = 'md',
  name,
  className = '',
  disabled = false,
  legend,
  legendHidden = false,
}) => {
  const generatedId = useId();
  const groupName = name || `hi-ui-card-selector-${generatedId}`;
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);

  // Normalize value to array for easier handling
  const selectedValues = useMemo<string[]>(
    () => (Array.isArray(value) ? value : value !== undefined ? [value] : []),
    [value]
  );

  const isSelected = (optionId: string): boolean => {
    return selectedValues.includes(optionId);
  };

  const handleChange = useCallback(
    (optionId: string, checked: boolean) => {
      if (!onChange) return;

      if (multiple) {
        // Checkbox mode: toggle the option
        const newValue = checked
          ? [...selectedValues, optionId]
          : selectedValues.filter((v) => v !== optionId);
        onChange(newValue);
      } else {
        // Radio mode: select single option
        onChange(optionId);
      }
    },
    [onChange, multiple, selectedValues]
  );

  // Keyboard navigation for radio mode
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, currentIndex: number) => {
      if (multiple) return; // Let native checkbox behavior handle it

      const enabledIndices = options
        .map((opt, idx) => ({ disabled: opt.disabled || disabled, idx }))
        .filter((item) => !item.disabled)
        .map((item) => item.idx);

      if (enabledIndices.length === 0) return;

      const currentEnabledIndex = enabledIndices.indexOf(currentIndex);
      let nextIndex: number;

      switch (e.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          e.preventDefault();
          nextIndex = enabledIndices[(currentEnabledIndex + 1) % enabledIndices.length];
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          e.preventDefault();
          nextIndex =
            enabledIndices[
              (currentEnabledIndex - 1 + enabledIndices.length) % enabledIndices.length
            ];
          break;
        default:
          return;
      }

      const nextCard = cardsRef.current[nextIndex];
      const input = nextCard?.querySelector('input');
      if (input) {
        input.focus();
        input.click();
      }
    },
    [options, multiple, disabled]
  );

  const wrapperClasses = [
    'hi-ui-card-selector',
    `hi-ui-card-selector--cols-${columns}`,
    `hi-ui-card-selector--${size}`,
    disabled ? 'hi-ui-card-selector--disabled' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const content = (
    <div className={wrapperClasses} role="group" data-testid="card-selector">
      {options.map((option, index) => {
        const optionDisabled = disabled || option.disabled;
        const checked = isSelected(option.id);

        const cardClasses = [
          'hi-ui-card-selector__card',
          checked ? 'hi-ui-card-selector__card--selected' : '',
          optionDisabled ? 'hi-ui-card-selector__card--disabled' : '',
        ]
          .filter(Boolean)
          .join(' ');

        const labelContent = (
          <span className="hi-ui-card-selector__content">
            {option.icon && (
              <span className="hi-ui-card-selector__icon" aria-hidden="true">
                {option.icon}
              </span>
            )}
            <span className="hi-ui-card-selector__text">
              <span className="hi-ui-card-selector__label">{option.label}</span>
              {option.description && (
                <span className="hi-ui-card-selector__description">{option.description}</span>
              )}
            </span>
          </span>
        );

        const InputComponent = multiple ? Checkbox : Radio;

        return (
          <div
            key={option.id}
            ref={(el) => {
              cardsRef.current[index] = el;
            }}
            className={cardClasses}
            data-testid={`card-selector-option-${option.id}`}
          >
            <InputComponent
              name={groupName}
              value={option.id}
              checked={checked}
              disabled={optionDisabled}
              onChange={(e) => handleChange(option.id, e.target.checked)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              size={size}
              className="hi-ui-card-selector__input"
            >
              {labelContent}
            </InputComponent>
          </div>
        );
      })}
    </div>
  );

  // Wrap in fieldset if legend is provided
  if (legend) {
    return (
      <fieldset className="hi-ui-card-selector__fieldset">
        <legend
          className={
            legendHidden
              ? 'hi-ui-card-selector__legend hi-ui-card-selector__legend--hidden'
              : 'hi-ui-card-selector__legend'
          }
        >
          {legend}
        </legend>
        {content}
      </fieldset>
    );
  }

  return content;
};
